import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { RegistrationFormComponent } from 'src/app/components/registration-form/registration-form.component';
import { CreateUser, createuserdefaultvalues } from 'src/app/components/users/createuser';
import { BbInvitationService } from 'src/app/services/bb-invitation.service';
import { ProvinceService } from 'src/app/services/province.service';
import { ToastService } from 'src/app/services/toast-service.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';

const LOG_PREFIX = '[BB Self-Guided Registration]';

@Component({
    selector: 'app-bb-self-guided-final-step',
    templateUrl: './bb-self-guided-final-step.component.html',
    styleUrls: ['./bb-self-guided-final-step.component.scss'],
    standalone: false
})
export class BbSelfGuidedFinalStepComponent implements OnInit {

  @ViewChild(RegistrationFormComponent) registrationForm!:RegistrationFormComponent;
  createuserobj:CreateUser=createuserdefaultvalues();
  inviteguid:string=''
  isSubmitted:boolean=false
  provinces:any[]=[]
  provincePrefilled:boolean=false
  selfGuidedYouthAgeLimit: string = '';
  isRegistering = false;
  translations: Record<string, string> = {};

  constructor(
    private _userservice:UserService,
    private route:ActivatedRoute,
    public router:Router,
    private _bbinviteservice:BbInvitationService,
    private _provinceservice:ProvinceService,
    private _toastservice:ToastService,
    private translationService: TranslationService
  ) { }

  ngOnInit(){
    this.translationService.getTranslation().subscribe({
      next: (translations) => {
        this.translations = translations;
      }
    });

    this.loadParticipantRegistrationConfig();

    this._provinceservice.getProvincesForRegistration().subscribe({
      next: (data) => {
        this.provinces = data;
      },
      error: (err: HttpErrorResponse) => {
        console.error(LOG_PREFIX, 'Failed to load provinces', err);
      }
    });

    this.route.params.subscribe(params => {
      this.inviteguid = params['guid'];
      this._bbinviteservice.getEmailFromBBSelfGuidedInvitationGuid(this.inviteguid).subscribe({
        next: (response) => {
          this.createuserobj.userName = response.email;
          if (response.provinceId != null && response.provinceId > 0) {
            this.createuserobj.province = response.provinceId;
            this.provincePrefilled = true;
          }
          console.log(LOG_PREFIX, 'Invitation loaded', {
            inviteguid: this.inviteguid,
            email: response.email,
            provinceId: response.provinceId
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(LOG_PREFIX, 'Failed to load invitation', err);
          this._toastservice.showErrorMessage(
            this.translations['sgpFinalInviteLoadError']
              ?? 'Could not load your invitation. Please use the link from your email again.'
          );
        }
      });
    });
  }

  private loadParticipantRegistrationConfig(): void {
    this._userservice.getParticipantRegistrationConfig().subscribe({
      next: (config) => {
        this.selfGuidedYouthAgeLimit = config.selfGuidedYouthAgeLimit ?? '';
        console.log(LOG_PREFIX, 'Backend config', {
          selfGuidedYouthAgeLimit: this.selfGuidedYouthAgeLimit
        });
      },
      error: (err: HttpErrorResponse) => {
        console.error(LOG_PREFIX, 'Failed to load backend config (selfGuidedYouthAgeLimit)', err);
      }
    });
  }

  onSubmit(){
    this.isSubmitted=true;
    if(!this.registrationForm.validateRequiredFields() || !this.createuserobj.province)
    {
      this._toastservice.showErrorMessage(
        this.translations['sgpFinalFormErrors'] ?? 'Please correct all the errors on the form'
      );
      return;
    }

    if (this.isRegistering) {
      return;
    }

    this.isRegistering = true;
    console.log(LOG_PREFIX, 'Submitting registration', {
      userName: this.createuserobj.userName,
      province: this.createuserobj.province,
      age: this.createuserobj.age,
      selfGuidedYouthAgeLimit: this.selfGuidedYouthAgeLimit
    });

    this._userservice.registerBBSelfGuidedParticipant(this.createuserobj).subscribe({
      next: (response) => {
        this.isRegistering = false;
        console.log(LOG_PREFIX, 'Registration response', response);

        if (response?.status === false) {
          const message = response.message ?? 'Registration could not be completed.';
          console.warn(LOG_PREFIX, 'Registration rejected', message);
          this._toastservice.showErrorMessage(message);
          if (response.redirectTo) {
            this.router.navigateByUrl('/' + response.redirectTo.replace(/^\//, ''));
          }
          return;
        }

        this.router.navigateByUrl("/account-created/bb");
      },
      error: (err: HttpErrorResponse) => {
        this.isRegistering = false;
        this.logRegistrationError(err);
        this._toastservice.showErrorMessage(this.extractErrorMessage(err));
      }
    });
  }

  private logRegistrationError(err: HttpErrorResponse): void {
    console.error(LOG_PREFIX, 'Registration HTTP error', {
      status: err.status,
      statusText: err.statusText,
      url: err.url,
      message: err.message,
      errorBody: err.error,
      selfGuidedYouthAgeLimit: this.selfGuidedYouthAgeLimit
    });
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const fallback = "We couldn't create your account. Please try again, or contact support if the problem persists.";
    const body = err?.error;

    if (body && typeof body === 'object') {
      if (typeof body.message === 'string' && body.message.length > 0) {
        return body.message;
      }
      if (body.errors && typeof body.errors === 'object') {
        const messages: string[] = [];
        for (const key of Object.keys(body.errors)) {
          const value = body.errors[key];
          if (Array.isArray(value)) {
            messages.push(...value);
          } else if (typeof value === 'string') {
            messages.push(value);
          }
        }
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
      if (typeof body.title === 'string' && body.title.length > 0) {
        return body.title;
      }
    }
    if (typeof body === 'string' && body.length > 0) {
      return body;
    }
    if (err.status === 0) {
      return 'Unable to reach the server (network or CORS). Check the API is running and allows this site origin.';
    }
    if (err.status === 409 && body && typeof body === 'object' && typeof body.message === 'string') {
      return body.message;
    }
    return fallback;
  }
}
